/**
 * Section 09c — Vajra Strategy.
 *
 * The rule itself lives in utils/strategies/vajraEngine.ts, a byte-identical
 * twin of backend/services/strategies/vajraEngine.ts. Every number on this page
 * is recomputed by backend/src/scripts/swing/verifyVajra.ts — if one changes
 * there it has to change here, which is the point of writing them down.
 */
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_vajra: GuideSection = {
  id: 'strategy-vajra',
  label: 'Vajra Strategy',
  group: 'strategies',
  icon: <BoltIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Vajra Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The BBR×Wave oversold buy, taken <strong>only when the higher timeframe is falling too</strong> — and has
        been for at least two bars. Rare by design: about three signals per symbol per year on 4-hour bars.
      </Typography>

      <GuideCallout kind="warning" title="Chart marks only — Vajra never places an order">
        There is no scan, no alert and no auto-trade behind this strategy. It draws a ▲ where it would buy and an
        ✕ where it would exit, and that is all. Confirm by eye and place the trade yourself.
      </GuideCallout>

      <H id="vajra-rule">The rule, in full</H>
      <P>
        <strong>Buy</strong> when BBR is at or below −100, Wave is at or below −60, BBR is rising against the
        previous bar — that is the existing BBR×Wave buy, unchanged — <strong>and</strong> the mapped higher
        timeframe has extended at least two bars in its current direction since its last colour change.
      </P>
      <P>
        <strong>Exit</strong> on whichever comes first: BBR back to the midline, a 2.5×ATR stop, or 20 bars.
      </P>
      <P>
        A 4-hour chart is gated by the daily; a daily chart by the weekly. That is the app's own higher-timeframe
        ladder, so the timeframe Vajra consults is the one the chart would show you above this one.
      </P>

      <GuideCallout kind="info" title="What the gate really means">
        At an oversold entry the higher timeframe's run is almost always a <em>down</em> run — 1,019 of 1,025
        occurrences. So in plain terms the gate says <strong>&ldquo;the higher timeframe is falling too, and has
        been for a while&rdquo;</strong>. It is a deeper-oversold condition across two timeframes, not a trend
        filter. Calling it &ldquo;higher highs&rdquo; would name the half that essentially never happens.
      </GuideCallout>

      <H id="vajra-record">What it measured</H>
      <P>
        47 crypto symbols, 487,000 four-hour bars, 2019–2026, after 0.20% round-trip costs, behind a scrambled-data
        control that had to clear its own false-discovery rate before any result was read.
      </P>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Gate</TableCell><TableCell align="right">Signals</TableCell>
            <TableCell align="right">Profit / signal</TableCell><TableCell align="right">Made money</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>off (the plain BBR×Wave buy)</TableCell><TableCell align="right">2,780</TableCell><TableCell align="right">+0.10%</TableCell><TableCell align="right">53.7%</TableCell></TableRow>
          <TableRow><TableCell>run ≥ 1</TableCell><TableCell align="right">1,928</TableCell><TableCell align="right">+0.19%</TableCell><TableCell align="right">54.9%</TableCell></TableRow>
          <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>run ≥ 2 — the default</TableCell><TableCell align="right">1,054</TableCell><TableCell align="right">+0.93%</TableCell><TableCell align="right">56.9%</TableCell></TableRow>
          <TableRow><TableCell>run ≥ 3</TableCell><TableCell align="right">496</TableCell><TableCell align="right">+1.85%</TableCell><TableCell align="right">60.7%</TableCell></TableRow>
          <TableRow><TableCell>run ≥ 4</TableCell><TableCell align="right">242</TableCell><TableCell align="right">+2.84%</TableCell><TableCell align="right">66.5%</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        Quality rises and frequency falls at every step, smoothly. That smoothness is the strongest argument that
        the gate is real rather than a lucky threshold. <strong>The default is 2 because 2 is the value that was
        registered in advance and passed multiple-testing control</strong> — 3 and 4 look better and were not in
        the tested set, so treat them as your choice rather than as a measured promise.
      </P>
      <P>
        At the default the gate beat all 4,000 random selections of the same number of trades, won 4 of 4
        walk-forward periods, improved 36 of 47 symbols, and beat the ungated rule in seven of eight years —
        including 2022, where the ungated rule loses money.
      </P>

      <H id="vajra-rejected">Three things that did not work</H>
      <P>
        Recorded here so they are not tried again, and because two of them looked convincing first.
      </P>
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        <li><Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Requiring the higher-timeframe band to be green.</strong> This measured +1.48% against +0.10%
          and it was <strong>look-ahead</strong>. The higher-timeframe context band stamps each bar with the
          higher-timeframe body <em>containing</em> it — and that body closes in the bar&rsquo;s future. Correct
          as drawn context, invalid as a decision. Made causal, using the last higher-timeframe bar that had
          actually closed, it is worth +0.12% against +0.10%, i.e. nothing.
        </Typography></li>
        <li><Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Requiring the higher timeframe&rsquo;s oscillator to be oversold too.</strong> The obvious way
          to club two timeframes. +0.45% against +0.10%, and negative in three of eight years. On daily bars it
          looked spectacular (+10.20%) and that was entirely 2025–26.
        </Typography></li>
        <li><Typography variant="body2">
          <strong>A plain &ldquo;close above EMA50&rdquo; trend filter</strong>, measured the same way: −0.20%
          against +0.10% — worse than no filter. So Vajra is not trend-following in disguise.
        </Typography></li>
      </Box>

      <H id="vajra-stop">Why the stop is 2.5×ATR here</H>
      <P>
        On <em>daily</em> bars a wider 4×ATR stop is better, and clearly so. It does not carry over to Vajra,
        for a mechanical reason: a held trade blocks the next signal, so a wider stop holds longer and costs
        entries. Swept inside the engine the gated rule peaks at 2.5 — 1.5×ATR gives +0.54%, 2.5×ATR +0.93%,
        4.0×ATR +0.61%. The daily finding stands for the daily rule; it is not imported here on the strength of
        a different test.
      </P>

      <H id="vajra-size">Position sizing</H>
      <P>
        Because the stop is fixed at entry, the risk on a trade is known before you take it. Risk per trade is a
        setting (default 1%), and the size follows from it:
      </P>
      <P>
        <code>stop distance = 2.5 × ATR(14) at the entry bar</code><br />
        <code>quantity = (account × risk%) ÷ stop distance</code>
      </P>
      <P>
        The chart shows this as guidance. It does not place, size or approve anything.
      </P>

      <H id="vajra-use">Using it</H>
      <P>
        Pick <strong>Vajra</strong> from the Strategy chips on the Charts toolbar. A ▲ marks an entry; an ✕ marks
        the exit, coloured by why it closed — blue for the midline, amber for the stop, grey for the 20-bar limit.
        If the chart has no higher timeframe above it, Vajra marks nothing and says so, rather than showing you
        the ungated signal under its name.
      </P>
      <P>
        If you see no marks, that is usually correct. Three signals per symbol per year is the design. The way to
        see more is to look at more symbols, not to loosen the gate.
      </P>
      <OpenInApp to="/charts" label="Open Charts" />
    </>
  ),
};

export default Section_strategy_vajra;
